/**
 * Browser URL validation and rendering for the investor template forms
 * (`EditGoodbyeLetterSettingsDialog.tsx` and `InvestorTemplatesTab.tsx`).
 * Keep behavior aligned with the server's `_shared/goodbye-letter-url.ts`.
 *
 * Restricting to `http(s)` (or bare hostnames) prevents `javascript:`,
 * `data:`, etc. from sneaking into a legal notice. We never rebuild an
 * `https://...` from junk — unsafe values get a visible placeholder so the
 * defect is obvious.
 */

/**
 * Accepts a bare hostname (`www.example.com`) OR an `http(s)://` URL.
 * Allows query strings and fragments without requiring a trailing `/`.
 */
export const SAFE_URL_RE =
  /^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}([/?#].*)?$/i;

const URL_SCHEME_RE = /^[a-z]+:\/\//i;

export function isSafeUrl(stored: string): boolean {
  return SAFE_URL_RE.test(stored);
}

/**
 * Normalize a stored URL for rendering. Bare hostnames get an `https://`
 * prefix so PDF hyperlinks resolve. `http(s)://` URLs pass through. Anything
 * else (including `javascript:`, `data:`, malformed values) renders as the
 * placeholder `(invalid payment URL)` — never reconstructed into a
 * fake-looking `https://`. The DB value is untouched either way.
 */
export function renderUrl(stored: string): string {
  if (!isSafeUrl(stored)) {
    return '(invalid payment URL)';
  }
  return URL_SCHEME_RE.test(stored) ? stored : `https://${stored}`;
}
